import { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { useCardSelector } from "../../common/CardSelectorProvider";
import personaService from "../../../services/personaService";
import TopicCard from "../../topics/TopicCard";
import PersonaCard from "../../persona/PersonaCard";
import {
  extractValidationVariables,
  handleTopicVisibilities,
} from "../../../utils/topicHelper";
import PresentationConfigItem from "./PresentationConfigItem";
import { getLatestTopicByVisibility } from "../../../services/topicService";
import usePins from "../../../hooks/usePins";

const PresentationConfigurator = forwardRef(function PresentationConfigurator(
  { configuration, setConfiguration },
  ref
) {
  const [topics, setTopics] = useState([]);
  const [topicVersion, setTopicVersion] = useState({});
  const [personas, setPersonas] = useState([]);
  const { openCardSelector } = useCardSelector();
  const [isLoading, setIsLoading] = useState(true);
  const { sortPinnedFirst: sortTopics } = usePins("topics");
  const { sortPinnedFirst: sortPersonas } = usePins("personas");

  useImperativeHandle(ref, () => ({
    addConfig: async () => {
      await handleAddConfiguration();
    },
  }));

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      const [personas] = await Promise.all([personaService.getAll()]);
      const topics = await getLatestTopicByVisibility("presentation");

      const versions = topics.reduce((accumulator, topic) => {
        accumulator[topic.key] = topic.version;
        return accumulator;
      }, {});

      const sortedTopics = await handleTopicVisibilities(topics);

      setPersonas(personas);
      setTopics(sortedTopics);
      setTopicVersion(versions);
      setIsLoading(false);
    };
    fetchData();
  }, []);

  const handleAddConfiguration = async () => {
    const promptForTopic = (selectedPersona) => {
      openCardSelector({
        items: sortTopics(topics, (t) => t.key),
        CardComponent: TopicCard,
        title: "Select a Topic",
        onSelect: (selectedTopic) => {
          const validation = JSON.parse(
            selectedTopic?.metadata?.find((md) => md.key === "validation")
              ?.value || "{}"
          );
          setConfiguration([
            ...configuration,
            {
              topic: {
                name: validation?.title || selectedTopic.name,
                description: validation?.details || selectedTopic.description,
                id: selectedTopic.id,
                key: selectedTopic.key,
                version: topicVersion[selectedTopic.key][0] || 0,
                variables: extractValidationVariables(selectedTopic),
              },
              persona: {
                id: selectedPersona.id,
              },
            },
          ]);
        },
        highlightVersions: ["Presentation"],
      });
    };

    openCardSelector({
      items: sortPersonas(personas, (p) => p.id),
      CardComponent: PersonaCard,
      title: "Select a Persona",
      onSelect: (selectedPersona) => {
        setTimeout(() => {
          promptForTopic(selectedPersona);
        }, 500);
      },
    });
  };

  if (isLoading) {
    return <div></div>;
  }

  const updateConfig = (index, updatedConfig) => {
    const newCfg = [...configuration];
    newCfg[index] = updatedConfig;
    setConfiguration(newCfg);
  };

  const removeConfig = (index) => {
    setConfiguration(configuration.filter((_, i) => i !== index));
  };

  return (
    <div className="mt-0">
      {configuration.map((configuration, index) => (
        <PresentationConfigItem
          key={index}
          configuration={configuration}
          topics={topics}
          personas={personas}
          onUpdate={(updatedTopic) => updateConfig(index, updatedTopic)}
          onDelete={() => removeConfig(index)}
        />
      ))}
    </div>
  );
});

export default PresentationConfigurator;
